import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Textarea } from "@/components/ui/textarea"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useEffect, useState } from "preact/hooks"
import { Link, useRoute } from "wouter-preact"
import {
  AccountingInvoiceJson,
  ApplyCashApplicationRequest,
  CreditMemoDetail,
  CreditMemoLineData,
  InvoiceAssignmentInput,
  LedgerAccountJson,
  applyCashApplication,
  getCreditMemo,
  listAccountingInvoices,
  listLedgerAccounts,
  publishCreditMemo,
  updateCreditMemo,
} from "@/api/accounting.tsx"
import { credit_memo_line_product_service } from "@/accounting/product_service_mapping"
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  ExternalLink,
  FileText,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Send,
  X,
} from "lucide-react"
import { format, parseISO } from "date-fns"
import { exactCurrencyFormatter } from "@/utils"
import { toast } from "@/components/ui/use-toast"
import { useOrgSettings } from "@/org_settings/state"
import { check_detail_path } from "./check_path"

export function accounting_deep_link(integration: string | null, remote_key?: string, remote_record_no?: string): string | null {
  if (!remote_key && !remote_record_no) return null
  switch (integration) {
    case "sageintacct":
      return remote_key ? `https://www.intacct.com/ia/acct/ar/adjustment.phtml?key=${remote_key}` : null
    case "quickbooks":
      return remote_key ? `https://app.qbo.intuit.com/app/creditmemo?txnId=${remote_key}` : null
    case "xero":
      return remote_key ? `https://go.xero.com/AccountsReceivable/ViewCreditNote.aspx?creditNoteID=${remote_key}` : null
    default:
      return null
  }
}

/**
 * Build the cash-application request that retries a credit memo through the resumable apply flow.
 * Every credit memo belongs to a cash-application check, and the apply endpoint skips the publish
 * when the memo already has a remote key — so retrying through it publishes the memo (when needed)
 * AND creates the customer payment that applies it to the invoices. A publish-only retry leaves the
 * memo orphaned in the accounting system with nothing applied.
 */
export function build_retry_apply_request(
  cm: Pick<CreditMemoDetail, "check_id" | "data">,
  create_missing_items = false
): ApplyCashApplicationRequest {
  return {
    check_id: cm.check_id,
    customer_id: cm.data.customer_id,
    customer_name: cm.data.customer_name,
    // Book the payment on the memo's date — the payment date chosen on the original apply
    // (`date_created` mirrors it); the server falls back to the check date when absent.
    payment_date: cm.data.date_created || undefined,
    // Invoice links persisted by the original apply attempt are reloaded server-side.
    invoice_assignments: [],
    create_missing_items: create_missing_items || undefined,
    // Additional-discount lines are rebuilt from the account the original apply chose; without it
    // a retry would drop them and fail the balance check the original apply passed.
    additional_discount_account_id: cm.data.additional_discount_account_id || undefined,
    additional_discount_on_payment: cm.data.additional_discount_on_payment || undefined,
    book_bi_allowance: cm.data.book_bi_allowance || undefined,
  }
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("build_retry_apply_request", () => {
    const cm = {
      check_id: "check-1",
      data: {
        customer_id: "84892",
        customer_name: "3196 KeHE",
        date_created: "2026-07-27",
        lines: [],
      },
    }

    it("carries the check, customer, and payment date from the memo", () => {
      expect(build_retry_apply_request(cm)).toEqual({
        check_id: "check-1",
        customer_id: "84892",
        customer_name: "3196 KeHE",
        payment_date: "2026-07-27",
        invoice_assignments: [],
        create_missing_items: undefined,
      })
    })

    it("passes create_missing_items only when requested", () => {
      expect(build_retry_apply_request(cm, true).create_missing_items).toBe(true)
    })

    it("carries the additional-discount account the original apply booked to", () => {
      const with_account = { ...cm, data: { ...cm.data, additional_discount_account_id: "acct-4701" } }
      expect(build_retry_apply_request(with_account).additional_discount_account_id).toBe("acct-4701")
      expect(build_retry_apply_request(cm).additional_discount_account_id).toBeUndefined()
    })

    it("carries a discount the original apply took on the payment", () => {
      const on_payment = { ...cm, data: { ...cm.data, additional_discount_on_payment: true } }
      expect(build_retry_apply_request(on_payment).additional_discount_on_payment).toBe(true)
      expect(build_retry_apply_request(cm).additional_discount_on_payment).toBeUndefined()
    })

    it("omits an empty payment date so the server defaults to the check date", () => {
      expect(
        build_retry_apply_request({ ...cm, data: { ...cm.data, date_created: "" } }).payment_date
      ).toBeUndefined()
    })
  })

  describe("accounting_deep_link", () => {
    it("returns QuickBooks URL from txnId", () => {
      expect(accounting_deep_link("quickbooks", "123")).toBe(
        "https://app.qbo.intuit.com/app/creditmemo?txnId=123"
      )
    })

    it("returns Sage Intacct URL from remote_key", () => {
      expect(accounting_deep_link("sageintacct", "456")).toBe(
        "https://www.intacct.com/ia/acct/ar/adjustment.phtml?key=456"
      )
    })

    it("returns null when no remote_key or remote_record_no", () => {
      expect(accounting_deep_link("quickbooks")).toBeNull()
    })

    it("returns null for unknown integration", () => {
      expect(accounting_deep_link("unknown", "123")).toBeNull()
    })
  })
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "-"
  try {
    return format(parseISO(dateStr), "MM/dd/yyyy")
  } catch {
    return dateStr
  }
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return "-"
  try {
    return format(parseISO(dateStr), "MM/dd/yyyy h:mm a")
  } catch {
    return dateStr
  }
}

export function CreditMemoDetailPage() {
  const [, params] = useRoute("/credit-memo/:id")
  const creditMemoId = params?.id

  const [creditMemo, setCreditMemo] = useState<CreditMemoDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isPublishing, setIsPublishing] = useState(false)
  // Error from re-running the apply flow (e.g. the memo published but the payment failed).
  const [applyError, setApplyError] = useState<string | null>(null)
  // QuickBooks Product/Service items missing for the coded GL accounts — offers a one-click
  // "create them in QuickBooks" retry when the publish fails on unmapped accounts.
  const [missingItems, setMissingItems] = useState<string[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false)

  // Edit state
  const [editDescription, setEditDescription] = useState("")
  const [editReferenceNumber, setEditReferenceNumber] = useState("")
  const [editLines, setEditLines] = useState<CreditMemoLineData[]>([])
  const [editAssignments, setEditAssignments] = useState<Record<string, string>>({})
  const [accountingInvoices, setAccountingInvoices] = useState<AccountingInvoiceJson[]>([])
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccountJson[]>([])
  const orgSettings = useOrgSettings()

  useEffect(() => {
    if (creditMemoId) {
      loadCreditMemo()
    }
  }, [creditMemoId])

  async function loadCreditMemo() {
    setIsLoading(true)
    setError(null)

    const [cmResult, invoicesResult, accountsResult] = await Promise.all([
      getCreditMemo(creditMemoId!),
      listAccountingInvoices(),
      listLedgerAccounts(true),
    ])

    if (accountsResult.ok) {
      setLedgerAccounts(accountsResult.value.data)
    }

    if (cmResult.ok) {
      const cm = cmResult.value.data
      setCreditMemo(cm)
      initEditState(cm)
    } else {
      setError("Failed to load credit memo")
    }

    if (invoicesResult.ok) {
      setAccountingInvoices(invoicesResult.value.data)
    } else {
      toast({ title: "Failed to load accounting invoices", variant: "destructive" })
    }

    setIsLoading(false)
  }

  function initEditState(cm: CreditMemoDetail) {
    setEditDescription(cm.data.description || "")
    setEditReferenceNumber(cm.data.reference_number || "")
    setEditLines(cm.data.lines.map((l) => ({ ...l })))
    const assignMap: Record<string, string> = {}
    for (const a of cm.invoice_assignments) {
      if (a.accounting_invoice_id) {
        assignMap[a.deduction_id] = a.accounting_invoice_id
      }
    }
    setEditAssignments(assignMap)
  }

  function handleStartEdit() {
    if (creditMemo) {
      initEditState(creditMemo)
      setIsEditing(true)
    }
  }

  function handleCancelEdit() {
    setIsEditing(false)
    if (creditMemo) initEditState(creditMemo)
  }

  async function handleSave() {
    if (!creditMemo) return
    setIsSaving(true)

    const invoiceAssignments: InvoiceAssignmentInput[] = Object.entries(editAssignments)
      .filter(([_, aid]) => aid)
      .map(([did, aid]) => ({
        deduction_id: did,
        accounting_invoice_id: aid,
      }))

    const result = await updateCreditMemo(creditMemo.id, {
      description: editDescription,
      reference_number: editReferenceNumber,
      lines: editLines,
      invoice_assignments: invoiceAssignments,
    })

    setIsSaving(false)

    if (result.ok) {
      setCreditMemo(result.value.data)
      setIsEditing(false)
      toast({ title: "Credit memo updated" })
    } else {
      toast({ title: "Failed to save", variant: "destructive" })
    }
  }

  async function handlePublish(create_missing_items = false) {
    if (!creditMemo) return
    setIsPublishing(true)
    setMissingItems([])

    const result = await publishCreditMemo(creditMemo.id, create_missing_items ? { create_missing_items: true } : undefined)
    setIsPublishing(false)

    if (result.ok && result.value.data.published_at) {
      toast({ title: "Credit memo published successfully" })
      loadCreditMemo()
    } else {
      const errMsg = result.ok
        ? result.value.data.publish_error || "Unknown error"
        : "Failed to publish"
      if (result.ok) {
        setMissingItems(result.value.data.missing_product_service_items ?? [])
      }
      toast({ title: "Publish failed", description: errMsg, variant: "destructive" })
      loadCreditMemo()
    }
  }

  /**
   * Retry through the resumable cash-application apply flow: publish the memo (skipped when
   * already published) and create the payment that applies it to the invoices. A publish-only
   * retry would leave the memo unapplied in the accounting system.
   */
  async function handleRetryApply(create_missing_items = false) {
    if (!creditMemo) return
    setIsPublishing(true)
    setMissingItems([])
    setApplyError(null)

    const result = await applyCashApplication(build_retry_apply_request(creditMemo, create_missing_items))
    setIsPublishing(false)

    if (result.ok && result.value.data.payment_created) {
      toast({
        title: "Cash applied",
        description: "Credit memo published and the payment was applied to the invoices.",
      })
    } else if (result.ok) {
      const data = result.value.data
      setApplyError(data.error ?? "Unknown error")
      setMissingItems(data.missing_product_service_items ?? [])
      toast({
        title: data.credit_memo_published ? "Credit memo published, but the payment failed" : "Publish failed",
        description: data.error ?? "Unknown error",
        variant: "destructive",
      })
    } else {
      setApplyError(result.error.message || "Failed to apply cash")
      toast({ title: "Failed to apply cash", description: result.error.message, variant: "destructive" })
    }
    loadCreditMemo()
  }

  function handleLineChange(index: number, field: keyof CreditMemoLineData, value: string | number) {
    setEditLines((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  function handleRemoveLine(index: number) {
    setEditLines((prev) => prev.filter((_, i) => i !== index))
  }

  if (isLoading) {
    return (
      <div class="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error || !creditMemo) {
    return (
      <div class="container mx-auto py-8 text-center">
        <p class="text-red-600">{error || "Credit memo not found"}</p>
        <Link to="/accounting/checks">
          <Button variant="outline" class="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Checks
          </Button>
        </Link>
      </div>
    )
  }

  const isPublished = !!creditMemo.published_at
  const hasFailed = !!creditMemo.publish_error && !creditMemo.published_at
  const isDraft = !isPublished && !hasFailed

  return (
    <div class="container mx-auto py-8 max-w-4xl">
      {/* Header */}
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-4">
          <Link to={check_detail_path(creditMemo.check_number)}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div class="flex items-center gap-3">
              <h1 class="text-2xl font-bold">Credit Memo</h1>
              {isPublished && (
                <Badge class="bg-green-100 text-green-800">Published</Badge>
              )}
              {isDraft && !hasFailed && (
                <Badge variant="outline">Draft</Badge>
              )}
              {hasFailed && (
                <Badge variant="destructive">Failed</Badge>
              )}
            </div>
            <p class="text-gray-600">Check #{creditMemo.check_number}</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          {isDraft && !hasFailed && !isEditing && (
            <>
              <Button variant="outline" onClick={handleStartEdit}>
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </Button>
              <Popover open={confirmPublishOpen} onOpenChange={setConfirmPublishOpen}>
                <PopoverTrigger asChild>
                  <Button disabled={isPublishing} class="bg-blue-600 hover:bg-blue-700">
                    {isPublishing ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Publishing...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Publish</>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent class="w-80">
                  <div class="space-y-3">
                    <div class="font-medium">Publish Credit Memo</div>
                    <p class="text-sm text-gray-600">
                      This will create a credit memo in your accounting system. This action cannot be undone.
                    </p>
                    <div class="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setConfirmPublishOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        class="bg-blue-600 hover:bg-blue-700"
                        onClick={() => { setConfirmPublishOpen(false); handlePublish() }}
                      >
                        Confirm Publish
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}
          {hasFailed && !isEditing && (
            <>
              <Button variant="outline" onClick={handleStartEdit}>
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </Button>
              <Button onClick={() => void handleRetryApply()} disabled={isPublishing} class="bg-blue-600 hover:bg-blue-700">
                {isPublishing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Retrying...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Retry Publish & Apply</>
                )}
              </Button>
            </>
          )}
          {isEditing && (
            <>
              <Button variant="outline" onClick={handleCancelEdit}>
                <X className="h-4 w-4 mr-2" /> Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save</>
                )}
              </Button>
            </>
          )}
          {isPublished && (() => {
            const deepLink = accounting_deep_link(
              orgSettings.accounting_integration,
              creditMemo.data.remote_key,
              creditMemo.data.remote_record_no,
            )
            return deepLink ? (
              <a href={deepLink} target="_blank" rel="noopener noreferrer">
                <Button variant="outline">
                  <ExternalLink className="h-4 w-4 mr-2" /> View in Accounting System
                </Button>
              </a>
            ) : null
          })()}
        </div>
      </div>

      {/* Error banner */}
      {hasFailed && (
        <Card class="mb-6 border-red-200 bg-red-50">
          <CardContent class="pt-6">
            <div class="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <p class="font-medium text-red-800">Publishing failed</p>
                <p class="text-sm text-red-600 mt-1">{creditMemo.publish_error}</p>
                {missingItems.length > 0 && (
                  <div class="mt-3 flex flex-wrap items-center gap-3 border-t border-red-200 pt-3">
                    <span class="text-sm text-red-700">
                      MarginWiz can create {missingItems.length === 1 ? "this item" : "these items"} in QuickBooks for
                      you (a Service item per GL account, posting to that account):{" "}
                      <span class="font-medium">{missingItems.join(", ")}</span>
                    </span>
                    <Button
                      size="sm"
                      class="bg-green-600 hover:bg-green-700"
                      disabled={isPublishing}
                      onClick={() => void handleRetryApply(true)}
                    >
                      {isPublishing
                        ? "Creating…"
                        : `Create ${missingItems.length === 1 ? "item" : `${missingItems.length} items`} in QuickBooks & apply`}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Apply-flow error (e.g. memo published but the payment failed) — the publish error itself
          is already shown in the failed banner above, so only render this once published. */}
      {applyError && !hasFailed && (
        <Card class="mb-6 border-red-200 bg-red-50">
          <CardContent class="pt-6">
            <div class="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <p class="font-medium text-red-800">Applying failed</p>
                <p class="text-sm text-red-600 mt-1">{applyError}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Published but never applied: the payment that applies the memo to the invoices was not
          created (e.g. a publish-only retry, or the payment step failed). */}
      {isPublished && !creditMemo.check_applied && (
        <Card class="mb-6 border-amber-200 bg-amber-50">
          <CardContent class="pt-6">
            <div class="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div class="flex-1">
                <p class="font-medium text-amber-800">Credit memo published, but not applied to invoices</p>
                <p class="text-sm text-amber-700 mt-1">
                  The credit memo exists in your accounting system, but the payment that applies it (and the
                  check's cash) to the invoices hasn't been created yet.
                </p>
              </div>
              <Button
                size="sm"
                class="bg-green-600 hover:bg-green-700 shrink-0"
                disabled={isPublishing}
                onClick={() => void handleRetryApply()}
              >
                {isPublishing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying…</>
                ) : (
                  "Create payment & apply"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Card */}
      <Card class="mb-6">
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div class="grid grid-cols-2 gap-y-4 gap-x-8">
            <div>
              <div class="text-sm text-gray-500">Reference Number</div>
              {isEditing ? (
                <Input
                  value={editReferenceNumber}
                  onInput={(e) => setEditReferenceNumber((e.target as HTMLInputElement).value)}
                  class="mt-1"
                />
              ) : (
                <div class="font-medium">{creditMemo.data.reference_number || "-"}</div>
              )}
            </div>
            <div>
              <div class="text-sm text-gray-500">Customer</div>
              <div class="font-medium">{creditMemo.data.customer_name || creditMemo.data.customer_id}</div>
            </div>
            <div>
              <div class="text-sm text-gray-500">Date Created</div>
              <div class="font-medium">{formatDate(creditMemo.data.date_created)}</div>
            </div>
            <div>
              <div class="text-sm text-gray-500">
                {isPublished ? "Published At" : "Created At"}
              </div>
              <div class="font-medium">
                {isPublished
                  ? formatDateTime(creditMemo.published_at)
                  : formatDateTime(creditMemo.created_at)}
              </div>
            </div>
            {isPublished && creditMemo.data.remote_key && (
              <div>
                <div class="text-sm text-gray-500">Remote Key</div>
                <div class="font-medium font-mono text-sm">{creditMemo.data.remote_key}</div>
              </div>
            )}
            <div class="col-span-2">
              <div class="text-sm text-gray-500">Description</div>
              {isEditing ? (
                <Textarea
                  value={editDescription}
                  onInput={(e) => setEditDescription((e.target as HTMLTextAreaElement).value)}
                  class="mt-1"
                  rows={2}
                />
              ) : (
                <div class="font-medium">{creditMemo.data.description || "-"}</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Assignments */}
      {creditMemo.invoice_assignments.length > 0 && (
        <Card class="mb-6">
          <CardHeader>
            <CardTitle class="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice Assignments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Accounting Invoice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditMemo.invoice_assignments.map((a) => (
                  <TableRow key={a.deduction_id}>
                    <TableCell>{a.invoice_number}</TableCell>
                    <TableCell>{exactCurrencyFormatter(a.deduction_amount)}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Select
                          value={editAssignments[a.deduction_id] || ""}
                          onValueChange={(val) =>
                            setEditAssignments((prev) => ({
                              ...prev,
                              [a.deduction_id]: val,
                            }))
                          }
                        >
                          <SelectTrigger class="w-64">
                            <SelectValue placeholder="Select invoice..." />
                          </SelectTrigger>
                          <SelectContent>
                            {accountingInvoices.map((inv) => (
                              <SelectItem key={inv.id} value={inv.id}>
                                {inv.invoice_number} ({exactCurrencyFormatter(inv.total_amount)})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : a.accounting_invoice_number ? (
                        <span class="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          {a.accounting_invoice_number}
                        </span>
                      ) : (
                        <span class="text-gray-400">Not assigned</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Credit Memo Lines */}
      <Card class="mb-6">
        <CardHeader>
          <CardTitle>Credit Memo Lines</CardTitle>
          <CardDescription>GL account breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>GL Account</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Memo</TableHead>
                    <TableHead class="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editLines.map((line, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {line.gl_account_id ? (
                          <>
                            <div class="font-medium">{line.gl_account_id}</div>
                            <div class="text-sm text-gray-500">{line.gl_account_name || ""}</div>
                          </>
                        ) : (
                          <Input
                            placeholder="GL Account ID"
                            value={line.gl_account_id}
                            onInput={(e: Event) => handleLineChange(index, "gl_account_id", (e.target as HTMLInputElement).value)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={line.amount}
                          onInput={(e) => {
                            const cents = Math.round(parseFloat((e.target as HTMLInputElement).value) * 100)
                            handleLineChange(index, "amount", cents / 100)
                          }}
                          class="w-32"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={line.memo || ""}
                          onInput={(e) => handleLineChange(index, "memo", (e.target as HTMLInputElement).value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveLine(index)}>
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button
                variant="outline"
                size="sm"
                class="mt-3"
                onClick={() =>
                  setEditLines((prev) => [
                    ...prev,
                    { gl_account_id: "", amount: 0 } as CreditMemoLineData,
                  ])
                }
              >
                <Plus className="h-4 w-4 mr-1" /> Add Line
              </Button>
            </>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>GL Account</TableHead>
                  <TableHead>Memo</TableHead>
                  <TableHead class="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditMemo.data.lines.map((line, index) => {
                  const product_service = credit_memo_line_product_service(line, ledgerAccounts)
                  return (
                  <TableRow key={index}>
                    <TableCell>
                      <div class="font-medium">{line.gl_account_id}</div>
                      <div class="text-sm text-gray-500">{line.gl_account_name || ""}</div>
                      {product_service ? (
                        <div class="text-xs text-gray-500 mt-0.5">
                          Item: <span class="font-medium text-gray-600">{product_service}</span>
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>{line.memo || "-"}</TableCell>
                    <TableCell class="text-right">{exactCurrencyFormatter(line.amount)}</TableCell>
                  </TableRow>
                  )
                })}
                {creditMemo.data.lines.length > 0 && (
                  <TableRow class="font-bold border-t-2">
                    <TableCell>Total</TableCell>
                    <TableCell></TableCell>
                    <TableCell class="text-right">
                      {exactCurrencyFormatter(
                        creditMemo.data.lines.reduce((sum, l) => sum + Math.round(l.amount * 100), 0) / 100
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
